import { createContext, useContext, useState, useCallback } from 'react'

const TranscriptContext = createContext(null)

export function TranscriptProvider({ children }) {
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false)
  const [transcriptEntries, setTranscriptEntries] = useState([])
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [autoTranscriptionEnabled, setAutoTranscriptionEnabled] = useState(true)
  const [transcriptionSource, setTranscriptionSource] = useState('hybrid') // 'web-speech', 'twilio', 'hybrid'
  const [twilioTranscriptionEnabled, setTwilioTranscriptionEnabled] = useState(true)

  /**
   * Toggle transcript panel
   */
  const toggleTranscript = useCallback(() => {
    setIsTranscriptOpen(prev => !prev)
  }, [])

  /**
   * Add transcript entry
   */
  const addEntry = useCallback((speaker, text, type = 'local', source = 'web-speech') => {
    const entry = {
      speaker,
      text,
      type,
      source,
      timestamp: Date.now(),
    }
    setTranscriptEntries(prev => [...prev, entry])
    return entry
  }, [])

  /**
   * Clear transcript entries
   */
  const clearEntries = useCallback(() => {
    setTranscriptEntries([])
  }, [])

  /**
   * Get entries by type
   */
  const getEntriesByType = useCallback((type) => {
    return transcriptEntries.filter(entry => entry.type === type)
  }, [transcriptEntries])

  /**
   * Get entries by speaker
   */
  const getEntriesBySpeaker = useCallback((speaker) => {
    return transcriptEntries.filter(entry => entry.speaker === speaker)
  }, [transcriptEntries])

  /**
   * Export transcript as text
   */
  const exportAsText = useCallback(() => {
    let text = 'Interview Transcript\n'
    text += `Generated: ${new Date().toLocaleString()}\n`
    text += '='.repeat(60) + '\n\n'

    transcriptEntries.forEach(entry => {
      const time = new Date(entry.timestamp).toLocaleTimeString()
      text += `[${time}] ${entry.speaker}:\n${entry.text}\n\n`
    })

    return text
  }, [transcriptEntries])

  /**
   * Download transcript
   */
  const downloadTranscript = useCallback(() => {
    const text = exportAsText()
    const blob = new Blob([text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `transcript_${Date.now()}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [exportAsText])

  const value = {
    isTranscriptOpen,
    transcriptEntries,
    isTranscribing,
    autoTranscriptionEnabled,
    transcriptionSource,
    twilioTranscriptionEnabled,
    setIsTranscriptOpen,
    setIsTranscribing,
    setAutoTranscriptionEnabled,
    setTranscriptionSource,
    setTwilioTranscriptionEnabled,
    toggleTranscript,
    addEntry,
    clearEntries,
    getEntriesByType,
    getEntriesBySpeaker,
    exportAsText,
    downloadTranscript,
  }

  return <TranscriptContext.Provider value={value}>{children}</TranscriptContext.Provider>
}

export function useTranscript() {
  const context = useContext(TranscriptContext)
  if (!context) {
    throw new Error('useTranscript must be used within TranscriptProvider')
  }
  return context
}
